// src/components/PhoneCapture/compressImage.js
//
// A modern phone shoots 8–12 MP; a single gold photo lands at 4–8 MB and five of
// them on branch mobile data is painful. Downscaling to a long edge that still
// resolves a hallmark stamp cuts that to a few hundred KB.
//
// Non-images (a PDF picked from files) and already-small images pass through
// untouched, and any failure falls back to the original file — compression must
// never be the reason an upload does not happen.

const DEFAULT_MAX_EDGE = 2000;
const DEFAULT_QUALITY = 0.82;
const SKIP_BELOW_BYTES = 400 * 1024;

const loadBitmap = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });

/**
 * @param {File|Blob} file
 * @param {Object} [opts] { maxEdge, quality }
 * @returns {Promise<File|Blob>} a compressed JPEG, or the original file
 */
export default async function compressImage(file, opts = {}) {
  const maxEdge = opts.maxEdge || DEFAULT_MAX_EDGE;
  const quality = opts.quality || DEFAULT_QUALITY;

  if (!file || !file.type || !file.type.startsWith('image/')) return file;
  if (file.type === 'image/gif') return file;
  if (file.size && file.size < SKIP_BELOW_BYTES) return file;

  try {
    const img = await loadBitmap(file);
    const longest = Math.max(img.naturalWidth, img.naturalHeight);
    const scale = longest > maxEdge ? maxEdge / longest : 1;

    const width = Math.round(img.naturalWidth * scale);
    const height = Math.round(img.naturalHeight * scale);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, width, height);

    const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
    if (!blob) return file;
    // Re-encoding a small image can make it bigger; keep whichever is smaller.
    if (file.size && blob.size >= file.size) return file;

    const name = (file.name || `capture-${Date.now()}.jpg`).replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() });
  } catch (err) {
    console.warn('[compressImage] falling back to original file:', err);
    return file;
  }
}
